import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import { positionService } from '../services/positionService';
import { employeeService } from '../services/employeeService';
import { brigadeService } from '../services/brigadeService';
import { courseService } from '../services/courseService';
import { matrixService } from '../services/matrixService';
import { trainingEventService } from '../services/trainingEventService';
import { useAuth } from './AuthContext';
import { cacheService } from '../services/cacheService';
import { dataEvents, EVENTS } from '../hooks/useDataEvents';
import { appEvents, APP_EVENTS } from '../services/appEvents';

class SimpleCacheService {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.refreshTimeout = null;
  }

  getCacheKey(key, params = {}) {
    return `${key}|${JSON.stringify(params)}`;
  }

  get(key, ttl = 30 * 60 * 1000) {
    return cacheService.get(key, ttl);
  }

  set(key, data) {
    cacheService.set(key, data);
  }

  isStale(key, ttl = 30 * 60 * 1000) {
    return cacheService.isStale(key, ttl);
  }

  async getOrFetch(key, fetchFn, options = {}) {
    const { force = false, ttl = 30 * 60 * 1000 } = options;
    
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }
    
    if (!force) {
      const cached = this.get(key, ttl);
      if (cached !== null) {
        return cached;
      }
    }
    
    const promise = fetchFn()
      .then(data => {
        this.set(key, data);
        this.pendingRequests.delete(key);
        return data;
      })
      .catch(error => {
        this.pendingRequests.delete(key);
        throw error;
      });
    
    this.pendingRequests.set(key, promise);
    return promise;
  }

  invalidate(pattern) {
    cacheService.invalidate(pattern);
  }

  clear() {
    cacheService.clear();
    this.pendingRequests.clear();
  }
}

const localCacheService = new SimpleCacheService();

const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_DATA: 'SET_DATA',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ALL: 'CLEAR_ALL'
};

const initialState = {
  positions: { data: [], status: 'idle', error: null, lastUpdated: null },
  positionCategories: { data: [], status: 'idle', error: null, lastUpdated: null },
  brigades: { data: [], status: 'idle', error: null, lastUpdated: null },
  courses: { data: [], status: 'idle', error: null, lastUpdated: null },
  employees: { data: [], status: 'idle', error: null, lastUpdated: null },
  categories: { data: [], status: 'idle', error: null, lastUpdated: null },
  matrix: { data: { positions: [], brigades: [], assignments: {} }, status: 'idle', error: null, lastUpdated: null },
  trainingEvents: { data: [], status: 'idle', error: null, lastUpdated: null },
};

function dataCacheReducer(state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          status: 'loading',
          error: null
        }
      };
    case ACTION_TYPES.SET_DATA:
      return {
        ...state,
        [action.key]: {
          data: action.data,
          status: 'success',
          error: null,
          lastUpdated: new Date().toISOString()
        }
      };
    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          status: 'error',
          error: action.error
        }
      };
    case ACTION_TYPES.CLEAR_ALL:
      return { ...initialState };
    default:
      return state;
  }
}

export const DataCacheContext = createContext(null);

export const DataCacheProvider = ({ children }) => {
  const [state, dispatch] = useReducer(dataCacheReducer, initialState);
  const isInitialized = useRef(false);
  const isRefreshing = useRef(false);
  const mountedRef = useRef(true);
  const refreshCounter = useRef(0);
  const lastRefreshTime = useRef(0);
  
  const { isAuthenticated, loading: authLoading } = useAuth();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchData = useCallback(async (key, fetchFn, options = {}) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      const error = new Error('Требуется авторизация');
      error.status = 401;
      if (mountedRef.current) {
        dispatch({ type: ACTION_TYPES.SET_ERROR, key, error: 'Требуется авторизация' });
      }
      throw error;
    }
    
    const { force = false, ttl = 30 * 60 * 1000 } = options;
    const cacheKey = localCacheService.getCacheKey(key, {});
    
    try {
      if (mountedRef.current) {
        dispatch({ type: ACTION_TYPES.SET_LOADING, key });
      }
      
      const data = await localCacheService.getOrFetch(cacheKey, fetchFn, { force, ttl });
      
      if (mountedRef.current) {
        dispatch({ type: ACTION_TYPES.SET_DATA, key, data: data });
      }
      return data;
    } catch (error) {
      console.error(`Error fetching ${key}:`, error);
      if (mountedRef.current) {
        dispatch({ type: ACTION_TYPES.SET_ERROR, key, error: error.message });
      }
      throw error;
    }
  }, []);

  const fetchPositions = useCallback(async (options = {}) => {
    return fetchData('positions', async () => {
      const data = await positionService.getAll();
      
      let positionsArray = [];
      if (Array.isArray(data)) {
        positionsArray = data;
      } else if (data && data.positions && Array.isArray(data.positions)) {
        positionsArray = data.positions;
      } else if (data && data.data && Array.isArray(data.data)) {
        positionsArray = data.data;
      } else if (data && data.data && data.data.positions && Array.isArray(data.data.positions)) {
        positionsArray = data.data.positions;
      }
      
      return positionsArray.map(pos => ({
        id: pos.id,
        name: pos.name || pos.title || 'Без названия',
        category_id: pos.category_id || 1,
        category_name: pos.category || pos.category_name || 'Без категории',
        employees_count: pos.employees_count || 0,
        departments: pos.departments || [],
        employees_by_department: pos.employees_by_department || [],
        created_at: pos.created_at,
        updated_at: pos.updated_at      
      }));
    }, options);
  }, [fetchData]);

  const fetchPositionCategories = useCallback(async (options = {}) => {
    const { force = false } = options;
    const cacheKey = 'position_categories';
    
    if (!force) {
      const cached = localCacheService.get(cacheKey, 60 * 60 * 1000);
      if (cached) {
        if (mountedRef.current) {
          dispatch({ type: ACTION_TYPES.SET_DATA, key: 'positionCategories', data: cached });
        }
        return cached;
      }
    }
    
    return fetchData('positionCategories', async () => {
      const positionsState = state.positions;
      let positionsArray = [];
      
      if (positionsState.status === 'success' && positionsState.data.length > 0) {
        positionsArray = positionsState.data;
      } else {
        const data = await positionService.getAll();
        
        if (Array.isArray(data)) {
          positionsArray = data;
        } else if (data && data.positions && Array.isArray(data.positions)) {
          positionsArray = data.positions;
        } else if (data && data.data && Array.isArray(data.data)) {
          positionsArray = data.data;
        }
      }
      
      const categoriesMap = new Map();
      positionsArray.forEach(pos => {
        const categoryId = pos.category_id;
        const categoryName = pos.category || pos.category_name;
        if (categoryId && categoryName && !categoriesMap.has(categoryId)) {
          categoriesMap.set(categoryId, {
            id: categoryId,
            name: categoryName,
            sort_order: categoryId
          });
        }
      });
      
      const categories = Array.from(categoriesMap.values()).sort((a, b) => a.id - b.id);
      localCacheService.set(cacheKey, categories);
      
      return categories;
    }, options);
  }, [fetchData, state.positions]);

  const fetchBrigades = useCallback(async (options = {}) => {
    return fetchData('brigades', async () => {
      const data = await brigadeService.getAll();
      
      let brigadesArray = [];
      if (Array.isArray(data)) {
        brigadesArray = data;
      } else if (data && data.brigades && Array.isArray(data.brigades)) {
        brigadesArray = data.brigades;
      } else if (data && data.data && Array.isArray(data.data)) {
        brigadesArray = data.data;
      }
      
      return brigadesArray.map(brigade => ({
        id: brigade.id,
        name: brigade.name || `Бригада ${brigade.id}`,
        leader: brigade.leader || 'Не назначен',
        leader_id: brigade.leader_id || null,
        members: brigade.members?.length || 0
      }));
    }, options);
  }, [fetchData]);

  const fetchCourses = useCallback(async (options = {}) => {
    return fetchData('courses', async () => {
      const data = await courseService.getAll();
      
      let coursesArray = [];
      if (Array.isArray(data)) {
        coursesArray = data;
      } else if (data && data.courses && Array.isArray(data.courses)) {
        coursesArray = data.courses;
      } else if (data && data.data && Array.isArray(data.data)) {
        coursesArray = data.data;
      }
      
      const mappedCourses = coursesArray.map(course => {
        const mapped = {
          id: course.id,
          name: course.name || 'Без названия',
          type: course.type || null,
          direction: course.direction || null,
          category_id: course.category_id,
          category_name: course.category || course.category_name || 'Общие',
          subcategory: course.subcategory || null,
          legal_basis: course.legal_basis || null,
          duration_hours: course.duration_hours,
          periodicity_months: course.periodicity_months,
          description: course.description || null,
          created_at: course.created_at,
          updated_at: course.updated_at,
          is_show: course.is_show === true || course.is_show === 1,
        };
        
        return mapped;
      });
      
      return mappedCourses;
    }, options);
  }, [fetchData]);

  const fetchCategories = useCallback(async (options = {}) => {
    return fetchData('categories', async () => {
      const data = await courseService.getCategories();
      
      let categoriesArray = [];
      if (data && data.categories_with_details && Array.isArray(data.categories_with_details)) {
        categoriesArray = data.categories_with_details.map(cat => ({
          id: cat.id,
          name: cat.name,
          sort_order: cat.sort_order
        }));
      } else if (data && data.categories && Array.isArray(data.categories)) {
        categoriesArray = data.categories.map((name, index) => ({
          id: index + 1,
          name: name,
          sort_order: index + 1
        }));
      } else if (Array.isArray(data)) {
        categoriesArray = data;
      } else if (data && data.data && Array.isArray(data.data)) {
        categoriesArray = data.data;
      }
      
      return categoriesArray;
    }, options);
  }, [fetchData]);

  const fetchEmployees = useCallback(async (options = {}) => {
    return fetchData('employees', async () => {
      const data = await employeeService.getAll();
      
      let employeesArray = [];
      if (Array.isArray(data)) {
        employeesArray = data;
      } else if (data && data.employees && Array.isArray(data.employees)) {
        employeesArray = data.employees;
      } else if (data && data.data && Array.isArray(data.data)) {
        employeesArray = data.data;
      } else if (data && data.data && data.data.employees && Array.isArray(data.data.employees)) {
        employeesArray = data.data.employees;
      } else if (data && typeof data === 'object') {
        for (const key in data) {
          if (Array.isArray(data[key]) && data[key].length > 0) {
            employeesArray = data[key];
            break;
          }
        }
      }
      
      return employeesArray.map(emp => ({
        id: emp.id,
        last_name: emp.last_name || '',
        first_name: emp.first_name || '',
        middle_name: emp.middle_name || '',
        full_name: emp.full_name || [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean).join(' ') || '—',
        personnel_number: emp.personnel_number || '—',
        position_id: emp.position_id,
        position: typeof emp.position === 'object' 
          ? (emp.position?.name || '—') 
          : (emp.position || '—'),
        department_id: emp.department_id,
        department: typeof emp.department === 'object'
          ? (emp.department?.name || '—')
          : (emp.department || '—'),
        brigade: emp.brigade || 'Не указана',
        brigade_id: emp.brigade_id,
        status: emp.status || 'active',
        email: emp.email || '',
        phone: emp.phone || ''
      }));
    }, options);
  }, [fetchData]);

  const fetchMatrix = useCallback(async (options = {}) => {
    return fetchData('matrix', async () => {
      const data = await matrixService.getMatrix();
      
      const positionsList = data.positions || [];
      const brigadesList = data.brigades || [];
      
      const matrixData = {
        positions: positionsList,
        brigades: brigadesList,
        assignments: {}
      };
      
      positionsList.forEach(position => {
        if (position.courses && Array.isArray(position.courses)) {
          position.courses.forEach(course => {
            const courseId = course.courseId || course.id;
            if (courseId) {
              const key = `${courseId}_${position.id}_position`;
              matrixData.assignments[key] = course.assigned === true;
            }
          });
        }
      });
      
      brigadesList.forEach(brigade => {
        if (brigade.courses && Array.isArray(brigade.courses)) {
          brigade.courses.forEach(course => {
            const courseId = course.courseId || course.id;
            if (courseId) {
              const key = `${courseId}_${brigade.id}_brigade`;
              matrixData.assignments[key] = course.assigned === true;
            }
          });
        }
      });
      
      return matrixData;
    }, options);
  }, [fetchData]);

  const fetchTrainingEvents = useCallback(async (options = {}) => {
    return fetchData('trainingEvents', async () => {
      const data = await trainingEventService.getAll({}, options.force);
      return data;
    }, options);
  }, [fetchData]);

  const updateMatrixAssignment = useCallback(async (courseId, targetId, targetType, assigned) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, key: 'matrix' });
    
    try {
      if (targetType === 'position') {
        await matrixService.updatePositionAssignment(targetId, courseId, assigned);
      } else if (targetType === 'brigade') {
        await matrixService.updateBrigadeAssignment(targetId, courseId, assigned);
      }
      
      localCacheService.invalidate('trainings');
      localCacheService.invalidate('matrix');
      
      dataEvents.emit(EVENTS.MATRIX_UPDATED, { courseId, targetId, targetType, assigned });
      appEvents.emit(APP_EVENTS.MATRIX_UPDATED, { courseId, targetId, targetType, assigned });
      
      setTimeout(() => {
        fetchMatrix({ force: true });
      }, 500);
      
      return true;
    } catch (error) {
      console.error('Ошибка обновления матрицы:', error);
      throw error;
    }
  }, [fetchMatrix]);

  const refreshAllData = useCallback(async () => {
    const now = Date.now();
    if (now - lastRefreshTime.current < 5000) {
      return;
    }
    
    refreshCounter.current++;
    if (refreshCounter.current > 10) {
      console.error('[DataCache] Potential infinite loop detected, resetting counter');
      refreshCounter.current = 0;
      return;
    }
    
    if (isRefreshing.current) {
      return;
    }
    
    const token = localStorage.getItem('access_token');
    if (!token) return;
    
    isRefreshing.current = true;
    lastRefreshTime.current = now;
    
    try {
      localCacheService.invalidate('positions');
      localCacheService.invalidate('position_categories');
      localCacheService.invalidate('brigades');
      localCacheService.invalidate('courses');
      localCacheService.invalidate('categories');
      localCacheService.invalidate('employees');
      localCacheService.invalidate('matrix');
      localCacheService.invalidate('trainingEvents');
      
      await Promise.allSettled([
        fetchPositions({ force: true }),
        fetchPositionCategories({ force: true }),
        fetchBrigades({ force: true }),
        fetchCourses({ force: true }),
        fetchCategories({ force: true }),
        fetchEmployees({ force: true }),
        fetchMatrix({ force: true }),
        fetchTrainingEvents({ force: true })
      ]);
      
      setTimeout(() => {
        refreshCounter.current = 0;
      }, 1000);
      
    } catch (error) {
      console.error('[DataCache] Error refreshing data:', error);
    } finally {
      isRefreshing.current = false;
    }
  }, [fetchPositions, fetchPositionCategories, fetchBrigades, fetchCourses, fetchCategories, fetchEmployees, fetchMatrix, fetchTrainingEvents]);

  useEffect(() => {
    const forceRefreshEvents = [
      APP_EVENTS.ENROLLMENT_CREATED,
      APP_EVENTS.ENROLLMENT_CANCELLED,
      APP_EVENTS.EVENT_CREATED,
      APP_EVENTS.EVENT_UPDATED,
      APP_EVENTS.EVENT_DELETED,
      APP_EVENTS.TRAINING_ASSIGNED,
      APP_EVENTS.TRAINING_COMPLETED,
      APP_EVENTS.COURSE_CREATED,
      APP_EVENTS.COURSE_UPDATED,
      APP_EVENTS.EMPLOYEE_CREATED,
      APP_EVENTS.EMPLOYEE_UPDATED,
      APP_EVENTS.MATRIX_UPDATED
    ];

    const handleForceRefresh = (data) => {
      localCacheService.invalidate('trainingEvents');
      localCacheService.invalidate('trainingEvents_all');
      localCacheService.invalidate('training_event_');
      
      if (localCacheService.refreshTimeout) {
        clearTimeout(localCacheService.refreshTimeout);
      }
      
      localCacheService.refreshTimeout = setTimeout(() => {
        fetchTrainingEvents({ force: true });
        localCacheService.refreshTimeout = null;
      }, 1000);
    };

    const unsubscribers = forceRefreshEvents.map(event => 
      appEvents.on(event, handleForceRefresh)
    );

    return () => {
      unsubscribers.forEach(unsub => unsub());
      if (localCacheService.refreshTimeout) {
        clearTimeout(localCacheService.refreshTimeout);
      }
    };
  }, [fetchTrainingEvents]);

  useEffect(() => {
    const unsubscribe = appEvents.on(APP_EVENTS.DATA_CHANGED, (data) => {
      localCacheService.invalidate('trainings');
      localCacheService.invalidate('matrix');
      localCacheService.invalidate('trainingEvents');
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const unsubscribers = [
      appEvents.on(APP_EVENTS.TRAINING_COMPLETED, (data) => {
        localCacheService.invalidate('trainings');
        localCacheService.invalidate('matrix');
        setTimeout(() => {
          fetchTrainingEvents({ force: true });
          fetchMatrix({ force: true });
        }, 500);
      }),
      appEvents.on(APP_EVENTS.ENROLLMENT_CREATED, (data) => {
        localCacheService.invalidate('trainingEvents');
        setTimeout(() => {
          fetchTrainingEvents({ force: true });
        }, 500);
      }),
      appEvents.on(APP_EVENTS.COURSE_UPDATED, () => {
        localCacheService.invalidate('courses');
        localCacheService.invalidate('matrix');
        setTimeout(() => {
          fetchCourses({ force: true });
          fetchMatrix({ force: true });
        }, 500);
      }),
      appEvents.on(APP_EVENTS.EMPLOYEE_UPDATED, () => {
        localCacheService.invalidate('employees');
        setTimeout(() => {
          fetchEmployees({ force: true });
        }, 500);
      }),
      appEvents.on(APP_EVENTS.CACHE_INVALIDATED, (data) => {
        localCacheService.invalidate(data.cacheName || '');
        if (data.cacheName === 'trainingEvents') {
          setTimeout(() => {
            fetchTrainingEvents({ force: true });
          }, 500);
        }
      })
    ];

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [fetchTrainingEvents, fetchCourses, fetchMatrix, fetchEmployees]);

  useEffect(() => {
    if (authLoading) return;
    
    const token = localStorage.getItem('access_token');
    const isReallyAuthenticated = isAuthenticated && !!token;
    
    if (isReallyAuthenticated && !isInitialized.current) {
      isInitialized.current = true;
      
      Promise.allSettled([
        fetchPositions(),
        fetchPositionCategories(),
        fetchBrigades(),
        fetchCourses(),
        fetchCategories(),
        fetchEmployees(),
        fetchMatrix(),
        fetchTrainingEvents()
      ]).catch((error) => {
        console.error('[DataCache] Error loading initial data:', error);
      });
    } else if (!isReallyAuthenticated && isInitialized.current) {
      isInitialized.current = false;
      localCacheService.clear();
      dispatch({ type: ACTION_TYPES.CLEAR_ALL });
    }
  }, [isAuthenticated, authLoading, fetchPositions, fetchPositionCategories, fetchBrigades, fetchCourses, fetchCategories, fetchEmployees, fetchMatrix, fetchTrainingEvents]);

  const value = {
    positions: state.positions,
    positionCategories: state.positionCategories,
    brigades: state.brigades,
    courses: state.courses,
    employees: state.employees,
    categories: state.categories,
    matrix: state.matrix,
    trainingEvents: state.trainingEvents,
    fetchPositions,
    fetchPositionCategories,
    fetchBrigades,
    fetchCourses,
    fetchCategories,
    fetchEmployees,
    fetchMatrix,
    fetchTrainingEvents,
    updateMatrixAssignment,
    refreshAllData,
    invalidateCache: (pattern) => localCacheService.invalidate(pattern),
    clearCache: () => localCacheService.clear(),
  };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
};

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within DataCacheProvider');
  }
  return context;
};